import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./EditReceiptForm.css";
import { supabase } from "../SupabaseClient.tsx";
import ActionButton from "../components/ActionButton.tsx";
import Order, { Attachment } from "../types/Order.ts";
import { readEml } from "eml-parse-js";
import {
  PiCaretDown,
  PiCaretUp,
  PiSpinnerGap,
  PiTrash,
  PiDownloadSimple,
  PiArrowsOutSimple,
  PiArrowsInSimple,
  PiX,
  PiCheckCircle,
  PiWarning,
  PiPaperclip,
  PiPlus,
  PiFile,
} from "react-icons/pi";
import { Link, useNavigate } from "react-router-dom";
import Badge from "../components/Badge.tsx";
import InfoBanner from "../components/InfoBanner.tsx";
import BeneficiaryComboBox from "../components/BeneficiaryComboBox.tsx";
import {
  addMissingBeneficiaries,
  matchBeneficiary,
} from "../utils/beneficiaries.ts";

const EditReceiptForm = (props: {
  /* Data */
  order: Order | null;
  receiptUrl: string | null;
  fileName: string | undefined;

  /* Attachments - new prop for multiple attachments support */
  attachments?: Attachment[];

  /* Props for Detail View */
  detailView?: boolean;

  /* Show the receipt without letting it be changed, for an account whose access has ended */
  readOnly?: boolean;

  /* Callback for when attachments change (for parent to refresh) */
  onAttachmentsChange?: () => void;

  /* Callback for when the selected attachment changes */
  onAttachmentSelect?: (attachment: Attachment) => void;

  /* Props for Upload View */
  onDelete?: () => void;
  onFormChange?: (id: string, updatedValues: any) => void;
  duplicateFileName?: boolean;

  /* Processing state from parent (used in upload view) */
  processing?: boolean;
  processingError?: string | null;
  onRetry?: () => void;
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const detailView = props.detailView ?? true;
  // Only starts collapsed when not showing the detail view
  const [isCollapsed, setIsCollapsed] = useState(
    props.detailView ? false : true
  );
  // orderLoading: true when we're actively processing OR when order is null and no error
  const [orderLoading, setOrderLoading] = useState(
    props.processing ?? (props.order === null && !props.processingError)
  );
  const [reimbursements, setReimbursements] = useState<any[]>([]);
  const [loadingReimbursements, setLoadingReimbursements] = useState(false);
  const [isPdfReceipt, setIsPdfReceipt] = useState(false);
  const [isEmlReceipt, setIsEmlReceipt] = useState(false);
  const [emlHtmlContent, setEmlHtmlContent] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [formModified, setFormModified] = useState(false);
  const formModifiedRef = React.useRef(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDuplicateWarningDismissed, setIsDuplicateWarningDismissed] =
    useState(false);
  const [beneficiariesList, setBeneficiariesList] = useState<string[]>([]);
  const [amountDisplay, setAmountDisplay] = useState<string>(
    props.order?.amount != null ? Number(props.order.amount).toFixed(2) : ""
  );
  const navigate = useNavigate();

  // Attachment management state
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] =
    useState<Attachment | null>(null);
  const [showAttachmentDeleteConfirm, setShowAttachmentDeleteConfirm] =
    useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<
    string | null
  >(null);

  // Get the current list of attachments
  const currentAttachments = useMemo(
    () => props.attachments || props.order?.attachments || [],
    [props.attachments, props.order?.attachments]
  );

  // Set initial selected attachment
  useEffect(() => {
    if (currentAttachments.length > 0 && !selectedAttachmentId) {
      const primary =
        currentAttachments.find((a) => a.is_primary) || currentAttachments[0];
      setSelectedAttachmentId(primary.id);
    }
  }, [currentAttachments, selectedAttachmentId]);

  // Form state
  const [formValues, setFormValues] = useState({
    name: props.order?.name || null,
    merchant: props.order?.merchant || null,
    category: props.order?.category || null,
    purchase_date: props.order?.purchase_date || null,
    amount: props.order?.amount || null,
    beneficiary: props.order?.beneficiary || null,
    origin: props.order?.origin?.name || null,
    notes: props.order?.notes || null,
  });

  // Fetch beneficiaries list
  useEffect(() => {
    const fetchBeneficiaries = async () => {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("beneficiaries")
          .single();

        if (error) {
          console.error("Error fetching beneficiaries:", error);
        } else if (data && data.beneficiaries) {
          setBeneficiariesList(data.beneficiaries);
        }
      } catch (error) {
        console.error("Unexpected error fetching beneficiaries:", error);
      }
    };

    fetchBeneficiaries();
  }, []);

  // Update form values when order changes (from null to a value)
  useEffect(() => {
    if (props.order) {
      setFormValues({
        name: props.order.name || null,
        merchant: props.order.merchant || null,
        category: props.order.category || null,
        purchase_date: props.order.purchase_date || null,
        amount: props.order.amount || null,
        beneficiary: props.order.beneficiary || null,
        origin: props.order.origin?.name || null,
        notes: props.order.notes || null,
      });
      // Only format the amount display on initial load, not during user editing
      if (!formModifiedRef.current) {
        setAmountDisplay(
          props.order.amount != null ? Number(props.order.amount).toFixed(2) : ""
        );
      }
      setOrderLoading(false);
      setFormModified(false); // Reset form modified state when order changes
      formModifiedRef.current = false;
    }
  }, [props.order]);

  // Sync orderLoading with parent processing state
  useEffect(() => {
    if (props.processing !== undefined) {
      setOrderLoading(props.processing);
    } else if (!props.order && !props.processingError) {
      setOrderLoading(true);
    } else if (props.processingError) {
      setOrderLoading(false);
    }
  }, [props.processing, props.order, props.processingError]);

  // Detect if the receipt is a PDF, image, or EML
  useEffect(() => {
    if (props.receiptUrl) {
      const fileExtension =
        props.fileName?.split(".").pop()?.toLowerCase() || "";

      // Check for common image extensions or if URL contains image extension patterns
      const isImage =
        ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(fileExtension) ||
        /\.(jpe?g|png|gif|bmp|webp)(\?|$)/i.test(props.receiptUrl);

      // Check if it's an EML file
      const isEml =
        fileExtension === "eml" ||
        props.receiptUrl.toLowerCase().includes(".eml");

      // If it's not an image or EML, check if it's explicitly a PDF
      const isPdf =
        fileExtension === "pdf" ||
        props.receiptUrl.toLowerCase().includes(".pdf");

      setIsPdfReceipt(isPdf && !isImage && !isEml);
      setIsEmlReceipt(isEml);

      // If it's an EML file, fetch and parse it
      if (isEml) {
        fetchAndParseEml(props.receiptUrl);
      }
    }
  }, [props.receiptUrl, props.fileName]);

  // Function to fetch and parse EML file
  const fetchAndParseEml = async (url: string) => {
    try {
      const response = await fetch(url);
      const emlContent = await response.text();

      readEml(emlContent, (error, data) => {
        if (error) {
          console.error("Error parsing EML file:", error);
          setEmlHtmlContent("<p>Error parsing email content</p>");
          return;
        }

        // Extract HTML content from parsed EML
        if (data?.html) {
          setEmlHtmlContent(data.html);
        } else if (data?.text) {
          // If no HTML, use plain text wrapped in pre tag
          setEmlHtmlContent(
            `<pre style="white-space: pre-wrap; font-family: inherit;">${data.text}</pre>`
          );
        } else {
          setEmlHtmlContent("<p>No email content found</p>");
        }
        setIframeLoaded(true);
      });
    } catch (error) {
      console.error("Error fetching EML file:", error);
      setEmlHtmlContent("<p>Error loading email content</p>");
      setIframeLoaded(true);
    }
  };

  // Function to fetch reimbursements - memoized to use in dependency array
  const fetchReimbursements = useCallback(async () => {
    if (!props.order) return;

    setLoadingReimbursements(true);
    const { data, error } = await supabase
      .from("order_reimburse")
      .select("id, reimburse_date, reimburse_amount")
      .eq("order_id", props.order.id)
      .order("reimburse_date", { ascending: false });

    if (error) {
      console.error("Error fetching reimbursements:", error);
    } else {
      setReimbursements(data || []);
    }
    setLoadingReimbursements(false);
  }, [props.order]);

  // Fetch reimbursement history when order changes and it's a detail view
  useEffect(() => {
    if (
      props.order &&
      detailView &&
      (props.order.status.name === "Partially Reimbursed" ||
        props.order.status.name === "Reimbursed")
    ) {
      fetchReimbursements();
    }
  }, [props.order, detailView, fetchReimbursements]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { id, value } = e.target;
    const updatedValues = {
      ...formValues,
      [id]: value,
    };
    setFormValues(updatedValues);
    setFormModified(true); // Mark form as modified when user makes changes
    formModifiedRef.current = true;

    // Notify parent component of the change if callback exists
    if (props.onFormChange && props.order) {
      props.onFormChange(props.order.id, updatedValues);
    }
  };

  const handleBeneficiaryChange = (value: string) => {
    const updatedValues = {
      ...formValues,
      beneficiary: value,
    };
    setFormValues(updatedValues);
    setFormModified(true);
    formModifiedRef.current = true;

    // Notify parent component of the change if callback exists
    if (props.onFormChange && props.order) {
      props.onFormChange(props.order.id, updatedValues);
    }
  };

  const handleCategoryChange = (value: string) => {
    const updatedValues = {
      ...formValues,
      category: value,
    };
    setFormValues(updatedValues);
    setFormModified(true);
    formModifiedRef.current = true;

    // Notify parent component of the change if callback exists
    if (props.onFormChange && props.order) {
      props.onFormChange(props.order.id, updatedValues);
    }
  };

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const handleImageLoad = () => {
    setIframeLoaded(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!props.order) {
      console.error("Cannot submit form: order is null");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare update data
      const updateData: any = {
        name: formValues.name,
        merchant: formValues.merchant,
        category: formValues.category,
        purchase_date: formValues.purchase_date,
        amount:
          formValues.amount !== null
            ? parseFloat(formValues.amount.toString())
            : null,
        beneficiary: formValues.beneficiary,
        notes: formValues.notes,
      };

      // If the order status is "Draft" and we're in detail view,
      // update the status to "Saved" (ID 2)
      if (detailView && props.order.status["name"] === "Draft") {
        updateData.status_id = 2; // "Saved" status ID
      }

      // Update the order record
      const { error: updateError } = await supabase
        .from("orders")
        .update(updateData)
        .eq("id", props.order.id);

      if (updateError) {
        console.error("Error updating order:", updateError);
      } else {
        // Save a newly typed beneficiary so it shows up in the dropdown next time
        const updatedBeneficiaries = await addMissingBeneficiaries([
          formValues.beneficiary,
        ]);
        if (updatedBeneficiaries) {
          setBeneficiariesList(updatedBeneficiaries);
        }

        // Show success notification instead of navigating
        setShowSuccessNotification(true);
        setFormModified(false); // Reset form modified state after successful save

        // Hide the notification after 3 seconds
        setTimeout(() => {
          setShowSuccessNotification(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  const handleDeleteConfirm = async () => {
    // If onDelete prop is provided, call it
    if (props.onDelete) {
      props.onDelete();
      return;
    }

    if (!props.order) {
      console.error("Cannot delete: order is null");
      return;
    }

    // Original delete logic for database records
    try {
      // Get all attachments to delete from storage
      const attachmentsToDelete: string[] = [];

      // Use attachments prop if available, otherwise fall back to order.attachments
      if (props.attachments && props.attachments.length > 0) {
        attachmentsToDelete.push(
          ...props.attachments.map((a) => a.storage_path)
        );
      } else if (
        props.order.attachments &&
        props.order.attachments.length > 0
      ) {
        attachmentsToDelete.push(
          ...props.order.attachments.map((a) => a.storage_path)
        );
      }

      // Delete the order record (this will cascade delete order_attachments due to FK constraint)
      const { error } = await supabase
        .from("orders")
        .delete()
        .eq("id", props.order.id);

      if (error) {
        console.error("Error deleting order:", error);
      } else {
        // Delete all attachment files from storage
        if (attachmentsToDelete.length > 0) {
          const { error: storageError } = await supabase.storage
            .from("order_receipts")
            .remove(attachmentsToDelete);

          if (storageError) {
            console.error("Error deleting receipt files:", storageError);
          }
        }

        navigate("/receipt-library");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    }

    setShowDeleteConfirmation(false);
  };

  // Helper to get primary attachment filename
  const getPrimaryFilename = (): string => {
    if (props.attachments && props.attachments.length > 0) {
      const primary =
        props.attachments.find((a) => a.is_primary) || props.attachments[0];
      return primary.original_filename;
    }
    if (props.order?.attachments && props.order.attachments.length > 0) {
      const primary =
        props.order.attachments.find((a) => a.is_primary) ||
        props.order.attachments[0];
      return primary.original_filename;
    }
    return props.fileName || "receipt.pdf";
  };

  const handleDownloadReceipt = async () => {
    if (!props.receiptUrl) return;
    try {
      const response = await fetch(props.receiptUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = getPrimaryFilename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download receipt:", err);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const shouldShowExpenseHistory = () => {
    return (
      detailView &&
      props.order &&
      (props.order.status["name"] === "Partially Reimbursed" ||
        props.order.status["name"] === "Reimbursed")
    );
  };

  const canSubmitForm = () => {
    if (!props.order) return false;
    return formModified || props.order.status["name"] === "Draft";
  };

  const handleDismissDuplicateWarning = () => {
    setIsDuplicateWarningDismissed(true);
  };

  // Attachment management functions
  const handleAttachmentSelect = async (attachment: Attachment) => {
    setSelectedAttachmentId(attachment.id);
    setIframeLoaded(false);

    // Notify parent to load new attachment URL
    if (props.onAttachmentSelect) {
      props.onAttachmentSelect(attachment);
    }
  };

  const handleAddAttachment = async (files: FileList | null) => {
    if (!files || files.length === 0 || !props.order) return;

    setIsUploadingAttachment(true);

    try {
      // Get user ID
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        console.error("User not authenticated");
        setIsUploadingAttachment(false);
        return;
      }

      for (const file of Array.from(files)) {
        // Generate unique storage path
        const fileExtension = file.name.split(".").pop() || "";
        const storagePath = `${user.id
          }/${crypto.randomUUID()}.${fileExtension}`;

        // Upload file to storage
        const { error: uploadError } = await supabase.storage
          .from("order_receipts")
          .upload(storagePath, file, {
            contentType: file.type,
            upsert: false,
          });

        if (uploadError) {
          console.error("Error uploading file:", uploadError);
          continue;
        }

        // Create attachment record
        const { error: attachmentError } = await supabase
          .from("order_attachments")
          .insert({
            order_id: props.order.id,
            storage_path: storagePath,
            original_filename: file.name,
            mime_type: file.type,
            is_primary: currentAttachments.length === 0, // First attachment is primary
          });

        if (attachmentError) {
          console.error("Error creating attachment record:", attachmentError);
          // Clean up uploaded file
          await supabase.storage.from("order_receipts").remove([storagePath]);
        }
      }

      // Notify parent to refresh attachments
      if (props.onAttachmentsChange) {
        props.onAttachmentsChange();
      }
    } catch (error) {
      console.error("Error adding attachment:", error);
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleAttachmentDeleteClick = (attachment: Attachment) => {
    setAttachmentToDelete(attachment);
    setShowAttachmentDeleteConfirm(true);
  };

  const handleAttachmentDeleteCancel = () => {
    setAttachmentToDelete(null);
    setShowAttachmentDeleteConfirm(false);
  };

  const handleAttachmentDeleteConfirm = async () => {
    if (!attachmentToDelete) return;

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("order_receipts")
        .remove([attachmentToDelete.storage_path]);

      if (storageError) {
        console.error("Error deleting file from storage:", storageError);
      }

      // Delete attachment record (or it will cascade if order is deleted)
      const { error: dbError } = await supabase
        .from("order_attachments")
        .delete()
        .eq("id", attachmentToDelete.id);

      if (dbError) {
        console.error("Error deleting attachment record:", dbError);
      }

      // If deleted attachment was selected, select another one
      if (selectedAttachmentId === attachmentToDelete.id) {
        const remainingAttachments = currentAttachments.filter(
          (a) => a.id !== attachmentToDelete.id
        );
        if (remainingAttachments.length > 0) {
          const newPrimary =
            remainingAttachments.find((a) => a.is_primary) ||
            remainingAttachments[0];
          setSelectedAttachmentId(newPrimary.id);
          if (props.onAttachmentSelect) {
            props.onAttachmentSelect(newPrimary);
          }
        } else {
          setSelectedAttachmentId(null);
        }
      }

      // If deleted attachment was primary, set new primary
      if (attachmentToDelete.is_primary) {
        const remainingAttachments = currentAttachments.filter(
          (a) => a.id !== attachmentToDelete.id
        );
        if (remainingAttachments.length > 0) {
          await supabase
            .from("order_attachments")
            .update({ is_primary: true })
            .eq("id", remainingAttachments[0].id);
        }
      }

      // Notify parent to refresh attachments
      if (props.onAttachmentsChange) {
        props.onAttachmentsChange();
      }
    } catch (error) {
      console.error("Error deleting attachment:", error);
    } finally {
      setAttachmentToDelete(null);
      setShowAttachmentDeleteConfirm(false);
    }
  };

  const triggerFileInput = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,.jpg,.jpeg,.png,.eml";
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      handleAddAttachment(files);
    };
    input.click();
  };

  const getFileIcon = (filename: string) => {
    return <PiFile />;
  };

  return (
    <div className="edit-receipt">
      {/* Success Notification Banner */}
      {showSuccessNotification && (
        <div className="success-notification">
          <PiCheckCircle /> Receipt details successfully updated
        </div>
      )}

      {isFullscreen && props.receiptUrl && (
        <div className="fullscreen-overlay">
          <div className="fullscreen-controls">
            <button onClick={toggleFullscreen} className="fullscreen-close">
              <PiArrowsInSimple /> Exit Fullscreen
            </button>
          </div>
          <div className="fullscreen-content-wrapper">
            {isPdfReceipt ? (
              <iframe
                src={`${props.receiptUrl}#view=FitHeight`}
                title="Receipt Preview Fullscreen"
                className="fullscreen-iframe"
              />
            ) : isEmlReceipt ? (
              <iframe
                srcDoc={emlHtmlContent || ""}
                title="Email Receipt Preview Fullscreen"
                className="fullscreen-iframe"
                sandbox="allow-same-origin"
              />
            ) : (
              <img
                src={props.receiptUrl}
                alt="Receipt Preview Fullscreen"
                className="fullscreen-image"
              />
            )}
          </div>
        </div>
      )}
      <div
        className="edit-receipt-header"
        onClick={() => !orderLoading && setIsCollapsed(!isCollapsed)}
        style={{ cursor: orderLoading ? "default" : "pointer" }}
      >
        <div className="edit-receipt-header-content">
          <h2>
            {props.fileName ||
              (props.order
                ? props.order.origin["name"] === "Manual Upload"
                  ? getPrimaryFilename()
                  : `${props.order.origin.name} Order: ${props.order.purchase_id}`
                : "Uploaded Receipt")}
          </h2>
          {props.order ? (
            <Badge status={props.order.status["name"]} />
          ) : props.processingError ? (
            <Badge status="Error" />
          ) : (
            <>
              <Badge status="Processing" />
              <span className="edit-receipt-analyzing-text">Analyzing receipt...</span>
            </>
          )}
        </div>
        {!detailView && (
          <div className="edit-receipt-header-actions">
            {props.onDelete && (
              <div
                className="edit-receipt-header-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick();
                }}
                title="Delete receipt"
              >
                <PiTrash />
              </div>
            )}
            <div
              className={`edit-receipt-header-collapse ${orderLoading ? "edit-receipt-order-loading" : ""
                }`}
              onClick={() => !orderLoading && !props.processingError && setIsCollapsed(!isCollapsed)}
              style={{ cursor: orderLoading || props.processingError ? "default" : "pointer" }}
            >
              {orderLoading ? (
                <PiSpinnerGap className="spin-animation" />
              ) : props.processingError ? (
                <PiWarning style={{ color: "var(--red-500, #ef4444)" }} />
              ) : isCollapsed ? (
                <PiCaretDown />
              ) : (
                <PiCaretUp />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Processing error banner with retry — only show when not in detail view */}
      {!detailView && props.processingError && (
        <InfoBanner
          icon={<PiWarning size={20} />}
          color="red"
          ctaText="Retry"
          onCtaClick={props.onRetry}
        >
          <p>{props.processingError}</p>
        </InfoBanner>
      )}

      {/* Duplicate filename warning - only show when not in detail view and filename is duplicated */}
      {!detailView &&
        props.duplicateFileName &&
        !isDuplicateWarningDismissed && (
          <InfoBanner
            icon={<PiWarning size={20} />}
            color="yellow"
            ctaText="Dismiss"
            onCtaClick={handleDismissDuplicateWarning}
          >
            <p>
              You already have a receipt with the filename "{props.fileName}" in
              your library. Are you sure you'd like to continue?
            </p>
          </InfoBanner>
        )}

      {!isCollapsed && !props.processingError && (
        <div className="edit-receipt-form">
          <form className="form-content" onSubmit={handleSubmit}>
            <fieldset
              className="edit-receipt-fields"
              disabled={props.readOnly}
            >
              <div className="edit-field">
                <label htmlFor="name">Receipt Description</label>
                <input
                  type="text"
                  id="name"
                  placeholder="Enter description"
                  value={formValues.name || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="edit-field">
                <label htmlFor="merchant">Merchant</label>
                <input
                  type="text"
                  id="merchant"
                  placeholder="Enter merchant name"
                  value={formValues.merchant || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="edit-field">
                <label htmlFor="category">Category</label>
                <BeneficiaryComboBox
                  id="category"
                  value={formValues.category}
                  onChange={handleCategoryChange}
                  options={["Medical", "Vision", "Dental"]}
                  placeholder="Select or enter category"
                />
              </div>
              <div className="edit-field">
                <label htmlFor="purchase_date">
                  Purchase Date{" "}
                  <span style={{ color: "var(--color-grey-400)" }}>
                    (required)
                  </span>
                </label>
                <input
                  type="date"
                  id="purchase_date"
                  value={formValues.purchase_date || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="edit-field">
                <label htmlFor="amount">
                  Amount{" "}
                  <span style={{ color: "var(--color-grey-400)" }}>
                    (required)
                  </span>
                </label>
                <div className="amount-input-wrapper">
                  <span className="amount-prefix">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="amount"
                    placeholder="0.00"
                    value={amountDisplay}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow empty, digits, and one decimal point with up to 2 decimal places
                      if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                        setAmountDisplay(val);
                        const numVal = parseFloat(val);
                        const updatedValues = {
                          ...formValues,
                          amount: isNaN(numVal) ? null : numVal,
                        };
                        setFormValues(updatedValues);
                        setFormModified(true);
                        formModifiedRef.current = true;
                        if (props.onFormChange && props.order) {
                          props.onFormChange(props.order.id, updatedValues);
                        }
                      }
                    }}
                    onBlur={() => {
                      // Format to 2 decimal places when user leaves the field
                      if (amountDisplay && amountDisplay !== "") {
                        const parsedValue = parseFloat(amountDisplay);
                        if (!isNaN(parsedValue)) {
                          setAmountDisplay(parsedValue.toFixed(2));
                          setFormValues({
                            ...formValues,
                            amount: parsedValue,
                          });
                        }
                      }
                    }}
                    required
                  />
                </div>
              </div>
              <div className="edit-field">
                <div className="edit-field-label-row">
                  <label htmlFor="beneficiary">Beneficiary</label>
                  {!props.readOnly && (
                    <Link
                      to="/settings#beneficiaries"
                      className="edit-field-label-link"
                    >
                      Manage beneficiaries
                    </Link>
                  )}
                </div>
                <BeneficiaryComboBox
                  id="beneficiary"
                  value={formValues.beneficiary}
                  onChange={handleBeneficiaryChange}
                  options={beneficiariesList}
                  placeholder="Enter or select beneficiary"
                />
                {!props.readOnly &&
                  beneficiariesList.length > 0 &&
                  formValues.beneficiary?.trim() &&
                  !matchBeneficiary(formValues.beneficiary, beneficiariesList) && (
                    <span className="edit-field-hint">
                      "{formValues.beneficiary.trim()}" will be added to your
                      beneficiaries when saved.
                    </span>
                  )}
              </div>
              <div className="edit-field">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  placeholder="Enter notes"
                  value={formValues.notes || ""}
                  onChange={handleInputChange}
                />
              </div>
            </fieldset>
            <div className="submit-area">
              {detailView && !props.readOnly && (
                <ActionButton
                  onClick={() => { }} // Form will handle submission
                  text={isSubmitting ? "Saving..." : "Confirm details"}
                  secondary={false} // Changed to primary button
                  disabled={!canSubmitForm() || isSubmitting}
                />
              )}
              <div className="icon-actions">
                {detailView && (
                  <div className="save-icon" onClick={handleDownloadReceipt}>
                    <PiDownloadSimple />
                    <div className="save-label">Download</div>
                  </div>
                )}
                {detailView && !props.readOnly && (
                  <div className="trash-icon" onClick={handleDeleteClick}>
                    <PiTrash />
                    <div className="trash-label">Delete</div>
                  </div>
                )}
              </div>
            </div>
          </form>
          {!iframeLoaded && (
            <p className="receipt-preview">Loading receipt preview...</p>
          )}
          <div
            className="receipt-preview"
            style={{ display: iframeLoaded ? "block" : "none" }}
          >
            {props.receiptUrl ? (
              isPdfReceipt ? (
                <div className="pdf-container">
                  <div className="pdf-controls">
                    <button
                      onClick={toggleFullscreen}
                      className="fullscreen-button"
                    >
                      <PiArrowsOutSimple /> View Fullscreen
                    </button>
                  </div>
                  <iframe
                    src={`${props.receiptUrl}#view=FitHeight`}
                    title="Receipt Preview"
                    onLoad={handleIframeLoad}
                  />
                </div>
              ) : isEmlReceipt ? (
                <div className="eml-container">
                  <div className="pdf-controls">
                    <button
                      onClick={toggleFullscreen}
                      className="fullscreen-button"
                    >
                      <PiArrowsOutSimple /> View Fullscreen
                    </button>
                  </div>
                  <iframe
                    srcDoc={emlHtmlContent || ""}
                    title="Email Receipt Preview"
                    className="eml-iframe"
                    sandbox="allow-same-origin"
                  />
                </div>
              ) : (
                <div className="image-container">
                  <div className="pdf-controls">
                    <button
                      onClick={toggleFullscreen}
                      className="fullscreen-button"
                    >
                      <PiArrowsOutSimple /> View Fullscreen
                    </button>
                  </div>
                  <img
                    src={props.receiptUrl}
                    alt="Receipt Preview"
                    onLoad={handleImageLoad}
                    className="receipt-image"
                  />
                </div>
              )
            ) : (
              <p>Receipt URL is not available.</p>
            )}
          </div>
        </div>
      )}

      {/* Attachments Section - only show in detail view */}
      {detailView && !isCollapsed && (
        <div className="attachments-section">
          <div className="attachments-header">
            <h3>
              <PiPaperclip /> Attachments ({currentAttachments.length})
            </h3>
            {!props.readOnly && (
              <button
                className="add-attachment-button"
                onClick={triggerFileInput}
                disabled={isUploadingAttachment}
              >
                {isUploadingAttachment ? (
                  <PiSpinnerGap className="spin-animation" />
                ) : (
                  <PiPlus />
                )}
                Add File
              </button>
            )}
          </div>

          {currentAttachments.length === 0 ? (
            <div className="no-attachments">
              <p>
                {props.readOnly
                  ? "No attachments."
                  : 'No attachments yet. Click "Add File" to upload.'}
              </p>
            </div>
          ) : (
            <div className="attachments-list">
              {currentAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className={`attachment-item ${selectedAttachmentId === attachment.id ? "selected" : ""
                    }`}
                  onClick={() => handleAttachmentSelect(attachment)}
                >
                  <div className="attachment-info">
                    {getFileIcon(attachment.original_filename)}
                    <span
                      className="attachment-name"
                      title={attachment.original_filename}
                    >
                      {attachment.original_filename}
                    </span>
                    {attachment.is_primary && (
                      <span className="primary-badge">Primary</span>
                    )}
                  </div>
                  {!props.readOnly && (
                    <button
                      className="attachment-delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAttachmentDeleteClick(attachment);
                      }}
                      title="Delete attachment"
                    >
                      <PiTrash />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {shouldShowExpenseHistory() && (
        <div className="expense-history-container">
          <div className="expense-history-header-row">
            <h2 className="container-label-header">Expense History</h2>
            {/* Remaining reimbursement amount */}
            <div className="remaining-reimbursement">
              {props.order && (
                <>
                  Expense Remaining:&nbsp;
                  <span>
                    $
                    {(
                      props.order.amount -
                      reimbursements.reduce(
                        (sum, r) => sum + (parseFloat(r.reimburse_amount) || 0),
                        0
                      )
                    ).toFixed(2)}
                  </span>
                </>
              )}
            </div>
          </div>
          {loadingReimbursements ? (
            <p>Loading reimbursement history...</p>
          ) : (
            <div className="receipt-table">
              <table>
                <thead>
                  <tr>
                    <th>Date Reimbursed</th>
                    <th className="currency">Amount Reimbursed</th>
                  </tr>
                </thead>
                <tbody>
                  {reimbursements.map((reimbursement) => (
                    <tr key={reimbursement.id}>
                      <td>
                        {new Date(
                          reimbursement.reimburse_date
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          timeZone: "UTC",
                        })}
                      </td>
                      <td className="currency">
                        ${reimbursement.reimburse_amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-modal">
            <div className="delete-confirmation-header">
              <h3>Confirm Delete</h3>
              <button className="close-button" onClick={handleDeleteCancel}>
                <PiX />
              </button>
            </div>
            <div className="delete-confirmation-body">
              <p>
                Are you sure you want to delete this receipt? This action cannot
                be undone.
              </p>
              <div className="delete-confirmation-buttons">
                <button
                  className="delete-cancel-button"
                  onClick={handleDeleteCancel}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-button"
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Delete Confirmation Modal */}
      {showAttachmentDeleteConfirm && attachmentToDelete && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-modal">
            <div className="delete-confirmation-header">
              <h3>Delete Attachment</h3>
              <button
                className="close-button"
                onClick={handleAttachmentDeleteCancel}
              >
                <PiX />
              </button>
            </div>
            <div className="delete-confirmation-body">
              <p>
                Are you sure you want to delete "
                {attachmentToDelete.original_filename}"? This action cannot be
                undone.
              </p>
              <div className="delete-confirmation-buttons">
                <button
                  className="delete-cancel-button"
                  onClick={handleAttachmentDeleteCancel}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-button"
                  onClick={handleAttachmentDeleteConfirm}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditReceiptForm;
